﻿import { TagBorderMode } from '@fe/core';
import { Checkbox, RadioGroup, TextBox } from '@progress/kendo-react-inputs';
import { Fragment, useCallback } from 'react';
import SettingsSubSection from '../../../../../components/common/SettingsSubSection';
import tagFields from '../../../../../static/static-tag-fields';
import { UDViewTagSelectionEnum } from '../../../../../udView/udview';
import { ColorPickerWithDefault } from '../../../../components/inputs/ColorPickerWithDefault';
import { DropdownList, DropdownListWithValueField } from '../../../../components/inputs/DropdownListWithValueField';
import ZoomLevelSlider from '../../../../components/inputs/ZoomLevelSlider';
import { MIN_ZOOM } from '../../../../components/MaplibreMap';
import { useAppDispatch } from '../../../../hooks/store-hooks';
import { patchFocusedWorkspaceView } from '../../../../state/store';
import { RadioButtonList, useFormCheckboxValue } from '../../hooks/forms';
import { useWorkspaceView } from '../../hooks/use-view';
import {
    useViewCheckboxValue,
    useViewColorValue,
    useViewDropdownValue,
    useViewRadioValue,
    useViewSliderValue,
    useViewStringValue
} from '../../hooks/view-forms';

const tagLabelFieldOptions: DropdownList<UDViewTagSelectionEnum> = Object.entries(tagFields).map(([key, tag]) => ({
    text: (Number(key) as UDViewTagSelectionEnum) === UDViewTagSelectionEnum.None ? '' : `${tag.name}`,
    value: Number(key) as UDViewTagSelectionEnum
}));

const borderModeOptions: RadioButtonList<TagBorderMode> = [
    { value: TagBorderMode.none, label: 'None' },
    { value: TagBorderMode.likeText, label: 'Tag color' },
    { value: TagBorderMode.other, label: 'This color' }
];

const TAG_BACKGROUND_DEFAULT_COLOR = '#000000';
const TAG_BORDER_DEFAULT_COLOR = '#FFFFFF';
const TAG_TEXT_DEFAULT_COLOR = '#FFFFFF';

export default function TagSettings() {
    const preview = useFormCheckboxValue(true);
    const backgroundColor = useViewColorValue((v) => v.plane.tag.backgroundColor || TAG_BACKGROUND_DEFAULT_COLOR);
    const borderColor = useViewColorValue((v) => v.plane.tag.outlineColor || TAG_BORDER_DEFAULT_COLOR);
    const textColor = useViewColorValue((v) => v.plane.tag.textColor || TAG_TEXT_DEFAULT_COLOR);
    const drawBackground = useViewCheckboxValue((v) => v.plane.tag.showLabelBackground);
    const zoomLevel = useViewSliderValue((v) => v.plane.tag.zoomLevel ?? MIN_ZOOM);
    const disableTagLeader = useViewCheckboxValue((v) => v.plane.tag.disableTagLeader);
    const hideAirlineId = useViewCheckboxValue((v) => v.plane.tag.hideAirlineId);
    const enableAutoTag = useViewCheckboxValue((v) => v.plane.tag.enableAutoTag);
    const borderMode = useViewRadioValue(borderModeOptions, (v) => v.plane.tag.outlineMode);
    const labels = useWorkspaceView((v) => v.plane.tag.labels);
    const dispatch = useAppDispatch();

    const tagLabels = [
        {
            status: useViewCheckboxValue((v) => v.plane.tag.labels[0]?.enabled),
            label: useViewStringValue((v) => v.plane.tag.labels[0]?.label || ''),
            type: useViewDropdownValue(tagLabelFieldOptions, (v) => v.plane.tag.labels[0]?.type)
        },
        {
            label: useViewStringValue((v) => v.plane.tag.labels[1]?.label || ''),
            type: useViewDropdownValue(tagLabelFieldOptions, (v) => v.plane.tag.labels[1]?.type)
        },
        {
            status: useViewCheckboxValue((v) => v.plane.tag.labels[2]?.enabled),
            label: useViewStringValue((v) => v.plane.tag.labels[2]?.label || ''),
            type: useViewDropdownValue(tagLabelFieldOptions, (v) => v.plane.tag.labels[2]?.type)
        },
        {
            label: useViewStringValue((v) => v.plane.tag.labels[3]?.label || ''),
            type: useViewDropdownValue(tagLabelFieldOptions, (v) => v.plane.tag.labels[3]?.type)
        },
        {
            status: useViewCheckboxValue((v) => v.plane.tag.labels[4]?.enabled),
            label: useViewStringValue((v) => v.plane.tag.labels[4]?.label || ''),
            type: useViewDropdownValue(tagLabelFieldOptions, (v) => v.plane.tag.labels[4]?.type)
        },
        {
            label: useViewStringValue((v) => v.plane.tag.labels[5]?.label || ''),
            type: useViewDropdownValue(tagLabelFieldOptions, (v) => v.plane.tag.labels[5]?.type)
        },
        {
            status: useViewCheckboxValue((v) => v.plane.tag.labels[6]?.enabled),
            label: useViewStringValue((v) => v.plane.tag.labels[6]?.label || ''),
            type: useViewDropdownValue(tagLabelFieldOptions, (v) => v.plane.tag.labels[6]?.type)
        },
        {
            label: useViewStringValue((v) => v.plane.tag.labels[7]?.label || ''),
            type: useViewDropdownValue(tagLabelFieldOptions, (v) => v.plane.tag.labels[7]?.type)
        },
        {
            status: useViewCheckboxValue((v) => v.plane.tag.labels[8]?.enabled),
            label: useViewStringValue((v) => v.plane.tag.labels[8]?.label || ''),
            type: useViewDropdownValue(tagLabelFieldOptions, (v) => v.plane.tag.labels[8]?.type)
        },
        {
            label: useViewStringValue((v) => v.plane.tag.labels[9]?.label || ''),
            type: useViewDropdownValue(tagLabelFieldOptions, (v) => v.plane.tag.labels[9]?.type)
        },
        {
            status: useViewCheckboxValue((v) => v.plane.tag.labels[10]?.enabled),
            label: useViewStringValue((v) => v.plane.tag.labels[10]?.label || ''),
            type: useViewDropdownValue(tagLabelFieldOptions, (v) => v.plane.tag.labels[10]?.type)
        },
        {
            label: useViewStringValue((v) => v.plane.tag.labels[11]?.label || ''),
            type: useViewDropdownValue(tagLabelFieldOptions, (v) => v.plane.tag.labels[11]?.type)
        }
    ];

    const changeLabelStatus = useCallback(
        (enabled: boolean, ndx: number) => {
            dispatch(
                patchFocusedWorkspaceView((v) => {
                    v.plane.tag.labels[ndx].enabled = enabled;
                    v.plane.tag.labels[ndx + 1].enabled = enabled;
                })
            );
        },
        [labels]
    );

    return (
        <div className="d-flex flex-column gap-2 px-3 py-2">
            <Checkbox {...preview} className="flex-grow-1" label={'Show preview'}></Checkbox>
            {preview.value && (
                <div
                    className={'d-flex justify-content-center align-items-center py-4 mt-2'}
                    style={{ background: `url('img/tag_preview.png')`, height: '150px' }}>
                    <code
                        style={{
                            background: drawBackground.value ? backgroundColor.value : 'transparent',
                            color: textColor.value
                        }}
                        className={'p-2'}>
                        {tagLabels.map((label, index) => (
                            <div key={index}>
                                {label.status?.value === true ? (
                                    <div>
                                        <span>
                                            {label.label.value} [{tagFields[label.type.value].shortName}]
                                        </span>
                                        <span>
                                            {' '}
                                            {tagLabels[index + 1].label.value} [{tagFields[tagLabels[index + 1].type.value].shortName}]
                                        </span>
                                    </div>
                                ) : (
                                    <></>
                                )}
                            </div>
                        ))}
                    </code>
                </div>
            )}
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'max-content 75px 1fr 75px 1fr',
                    gridColumnGap: '0.5rem',
                    gridRowGap: '0.5rem'
                }}
                className={'mt-2'}>
                <span></span>
                <span>Label</span>
                <span>Field</span>
                <span>Label</span>
                <span>Field</span>

                {tagLabels.map((label, index) => (
                    <Fragment key={index}>
                        {label.status && (
                            <div className={'my-auto'}>
                                <Checkbox {...label.status} onChange={(e) => changeLabelStatus(e.value, index)} />
                            </div>
                        )}
                        <TextBox {...label.label} rounded={'large'} />
                        <DropdownListWithValueField {...label.type} rounded={'large'} filterable={true} />
                    </Fragment>
                ))}
            </div>
            <div className={'mt-2 secondary-text'}>
                * Field requires customer data feed, authorization or is provided for MyFleet flights only.
            </div>
            <SettingsSubSection title={'Tag background'}>
                <Checkbox {...drawBackground} className="flex-grow-1" label={'Draw background behind tag text'}></Checkbox>
                <div className="d-flex align-items-center justify-content-between gap-2 w-50">
                    <div>Background color:</div>
                    <ColorPickerWithDefault {...backgroundColor} defaultColor={TAG_BACKGROUND_DEFAULT_COLOR} />
                </div>
                <div className="d-flex align-items-center gap-2">
                    <div>Border mode:</div>
                    <RadioGroup {...borderMode} layout={'horizontal'} />
                </div>
                <div className="d-flex align-items-center justify-content-between gap-2 w-50">
                    <div>Border color:</div>
                    <ColorPickerWithDefault {...borderColor} defaultColor={TAG_BORDER_DEFAULT_COLOR} />
                </div>
                <div className="d-flex align-items-center justify-content-between gap-2 w-50">
                    <div>Text color:</div>
                    <ColorPickerWithDefault {...textColor} defaultColor={TAG_TEXT_DEFAULT_COLOR} />
                </div>
                <ZoomLevelSlider {...zoomLevel} className={'w-50'} />
            </SettingsSubSection>
            <SettingsSubSection title={'Tag properties'}>
                <Checkbox {...disableTagLeader} className="flex-grow-1" label={'Disable tag leader line'}></Checkbox>
                <Checkbox {...hideAirlineId} className="flex-grow-1" label={'Hide airline ID portion of aircraft ID'}></Checkbox>
                <Checkbox {...enableAutoTag} className="flex-grow-1" label={'Auto-arrange tags'}></Checkbox>
            </SettingsSubSection>
        </div>
    );
}
